import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@material-ui/styles";
import { useParams } from "react-router-dom";

import AdvertiserInfoCard from "../../components/redesign/modules/AdvertiserSettings/AdvertiserInfoCard";
import ArchiveAdvertiserModal from "../../components/redesign/modules/AdvertisersTable/ArchiveAdvertiserModal";
import BackButton from "../../components/redesign/elements/BackButton";
import BillingCard from "../../components/redesign/modules/AdvertiserSettings/BillingCard";
import HubSpotCard from "../../components/redesign/modules/AdvertiserSettings/HubSpotCard";
import InvoicesCard from "../../components/redesign/modules/AdvertiserSettings/InvoicesCard";
import Loader from "../../components/elements/Loader";
import api from "../../api";
import { AdvertiserType } from "../../types/advertiserTypes";
import { useSetPageMeta } from "../../layouts/AppShellLayout/PageMetaContext";

const fetchAdvertiserById = async (bgAdvertiserId: string): Promise<AdvertiserType> => {
  const { payload } = await api.getAdvertiserById(bgAdvertiserId);
  return payload;
};

const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 24,
});

const AdvertiserSettingsPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const [advertiser, setAdvertiser] = useState<AdvertiserType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [archiveTarget, setArchiveTarget] = useState<AdvertiserType | null>(null);

  const advertiserName = advertiser?.name ?? "Advertiser";
  useSetPageMeta("Advertiser Settings", advertiserName);

  const fetchAndSetAdvertiserDetailData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await fetchAdvertiserById(id);
      setAdvertiser(data);
    } catch (error) {
      console.log("AdvertiserSettingsPage fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    localStorage.setItem("currentBrandgenAdvertiserId", id);
    fetchAndSetAdvertiserDetailData();
  }, [id, fetchAndSetAdvertiserDetailData]);

  const handleArchive = () => {
    if (advertiser) setArchiveTarget(advertiser);
  };

  const handleUnarchive = async () => {
    try {
      await api.unarchiveAdvertiser(id);
      await fetchAndSetAdvertiserDetailData();
    } catch (error) {
      console.log("handleUnarchive error:", error);
    }
  };

  if (isLoading) return <Loader />;
  if (!advertiser) return null;

  return (
    <>
      <Root>
        <BackButton to="/advertisers" label="Back to Advertisers" />
        <AdvertiserInfoCard advertiser={advertiser} onArchive={handleArchive} onUnarchive={handleUnarchive} />
        <HubSpotCard advertiser={advertiser} fetchAndSetAdvertiserDetailData={fetchAndSetAdvertiserDetailData} />
        <BillingCard advertiser={advertiser} fetchAndSetAdvertiserDetailData={fetchAndSetAdvertiserDetailData} />
        <InvoicesCard advertiserId={advertiser.brandgenAdvertiserId} />
      </Root>

      {archiveTarget ? (
        <ArchiveAdvertiserModal
          advertiser={archiveTarget}
          onClose={() => setArchiveTarget(null)}
          onSuccess={fetchAndSetAdvertiserDetailData}
        />
      ) : null}
    </>
  );
};

export default AdvertiserSettingsPage;
